import type {Rule} from 'antd/es/form';

import {CommonErrors} from '../../../constants/Common';
import type {MaterialRow, StageId} from '../../../services/interface/pcfTypes';
import {GWP100_HORIZON_VALUE} from '../constants/pcfOptions';
import {ImpactConfigurationLabels} from '../PCFKeyLabels';

const BIO_BASED_MATERIAL_KEYWORDS = [
  'wood',
  'biomass',
  'agricultural',
  'bio-based',
  'bio based',
  'crop',
  'fibre',
  'fiber',
  'residue',
  'bamboo',
  'cotton',
  'hemp',
  'jute',
  'sugarcane',
  'palm',
  'corn',
  'wheat',
  'rice',
  'soy',
  'cellulose',
  'lignin',
  'biofuel',
  'timber',
  'lumber',
  'cork',
  'straw',
  'bagasse'
];

const B_OR_D_STAGES: StageId[] = [
  'B1',
  'B2',
  'B3',
  'B4',
  'B5',
  'B6',
  'B7',
  'D'
];

export const hasBioBasedMaterials = (materials?: MaterialRow[]): boolean => {
  if (!materials?.length) {
    return false;
  }

  return materials.some((material) => {
    const name = String(material.name ?? '').toLowerCase();

    if (!name.trim()) {
      return false;
    }

    return BIO_BASED_MATERIAL_KEYWORDS.some((keyword) =>
      name.includes(keyword)
    );
  });
};

export const hasSequestrationRelevantStages = (
  stages?: Partial<Record<StageId, boolean>>
): boolean => {
  if (!stages) {
    return false;
  }

  return B_OR_D_STAGES.some((stage) => Boolean(stages[stage]));
};

export const gwpHorizonRules = (expertMode: boolean): Rule[] => [
  {
    required: true,
    message: CommonErrors.mandatory(ImpactConfigurationLabels.GWP_TIME_HORIZON)
  },
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (!expertMode && value && value !== GWP100_HORIZON_VALUE) {
        throw new Error('GWP20 and GWP500 are available in Expert Mode only.');
      }

      await Promise.resolve();
    }
  }
];

export const biogenicHandlingRules = (): Rule[] => [
  {
    required: true,
    message: CommonErrors.mandatory(
      ImpactConfigurationLabels.BIOGENIC_CARBON_HANDLING
    )
  }
];

export const uncertaintyIterationsRules = (): Rule[] => [
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (value == null || value === '') {
        throw new Error(
          CommonErrors.mandatory(
            ImpactConfigurationLabels.UNCERTAINTY_ITERATIONS
          )
        );
      }

      const parsed = Number(value);

      if (!Number.isInteger(parsed) || parsed < 100 || parsed > 10000) {
        throw new Error(
          'Number of iterations must be an integer between 100 and 10,000.'
        );
      }

      await Promise.resolve();
    }
  }
];

export const isImpactConfigurationReady = (values: {
  lciaMethod?: string;
  gwpHorizon?: string;
  biogenicHandling?: string;
}): boolean =>
  Boolean(
    values.lciaMethod?.trim() &&
      values.gwpHorizon?.trim() &&
      values.biogenicHandling?.trim()
  );

export const MOCK_CALCULATION_DELAY_MS = 2500;
